import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, LoginModal } from '../common';
import { useCart } from '../../hooks/useCart';
import { useAuthStore } from '../../store/useAuthStore';
import { formatCurrency } from '../../utils/format';

const CartSummary: React.FC = () => {
  const navigate = useNavigate();
  const { subtotal, shipping, tax, total, itemCount } = useCart();
  const { isAuthenticated } = useAuthStore();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const freeShippingThreshold = 50;
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - subtotal);

  const handleCheckoutClick = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
    } else {
      navigate('/checkout');
    }
  };

  return (
    <div className="bg-dark-dark dark:bg-white rounded-xl shadow-md border border-dark-light dark:border-gray-100 p-4 md:p-6 sticky top-20 md:top-24 transition-colors duration-300">
      <h2 className="text-lg md:text-xl font-bold text-white dark:text-dark-dark mb-4 md:mb-6 transition-colors duration-300">
        Order Summary
      </h2>

      {/* Free Shipping Progress */}
      {subtotal > 0 && subtotal < freeShippingThreshold && (
        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-accent/10 dark:bg-primary/10 rounded-lg transition-colors duration-300">
          <p className="text-sm text-accent dark:text-primary font-medium mb-2 transition-colors duration-300">
            Add {formatCurrency(remainingForFreeShipping)} more for FREE shipping!
          </p>
          <div className="w-full bg-accent/30 dark:bg-primary/30 rounded-full h-2">
            <div
              className="bg-accent dark:bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${(subtotal / freeShippingThreshold) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Summary Items */}
      <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
        <div className="flex justify-between text-gray-400 dark:text-gray-500 text-sm md:text-base transition-colors duration-300">
          <span>Subtotal ({itemCount} items)</span>
          <span>{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex justify-between text-gray-400 dark:text-gray-500 text-sm md:text-base transition-colors duration-300">
          <span>Shipping</span>
          <span className={shipping === 0 ? 'text-accent dark:text-primary' : 'transition-colors duration-300'}>
            {shipping === 0 ? 'FREE' : formatCurrency(shipping)}
          </span>
        </div>
        <div className="flex justify-between text-gray-400 dark:text-gray-500 text-sm md:text-base transition-colors duration-300">
          <span>Tax (8%)</span>
          <span>{formatCurrency(tax)}</span>
        </div>
        <div className="border-t border-dark dark:border-gray-200 pt-2 md:pt-3 mt-2 md:mt-3 transition-colors duration-300">
          <div className="flex justify-between text-base md:text-lg font-bold text-white dark:text-dark-dark transition-colors duration-300">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>

      {/* Checkout Button */}
      <Button
        variant="primary"
        size="lg"
        className="w-full mb-3 md:mb-4"
        onClick={handleCheckoutClick}
        disabled={itemCount === 0}
      >
        Checkout
      </Button>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        redirectToCheckout
      />

      {/* Continue Shopping */}
      <Button
        variant="ghost"
        size="md"
        className="w-full"
        onClick={() => navigate('/products')}
      >
        Continue Shopping
      </Button>

      {/* Trust Badges */}
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-dark-light dark:border-gray-100 transition-colors duration-300">
        <div className="grid grid-cols-2 gap-2 md:gap-3 text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-accent dark:bg-primary rounded-full transition-colors duration-300" />
            <span>Secure Checkout</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-accent dark:bg-primary rounded-full transition-colors duration-300" />
            <span>Fresh Guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-accent dark:bg-primary rounded-full transition-colors duration-300" />
            <span>Eco-Friendly</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-accent dark:bg-primary rounded-full transition-colors duration-300" />
            <span>24/7 Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;